/**
 * Capability gate states — shared product system vocabulary (FM + BH).
 * Runtime limits still live in each app's plan-limits.ts + DB.
 */

export type CapabilityGateState =
  | "included"
  | "near_limit"
  | "limit_reached"
  | "upgrade_available"
  | "locked_feature";

export type PlanTierId = "starter" | "pro" | "team" | "business" | "enterprise" | "free_trial" | "all";

export const PLAN_TIER_RANK: Record<string, number> = {
  starter: 0,
  pro: 1,
  team: 1,
  business: 2,
  enterprise: 3,
  free_trial: 1,
  all: 0,
};

/** Labels canónicos de plan (no marketing / no "Professional"). */
const TIER_DISPLAY: Record<string, string> = {
  starter: "starter",
  pro: "PRO",
  team: "PRO",
  business: "business",
  enterprise: "enterprise",
  free_trial: "free_trial",
  all: "Todos los planes",
};

export function planIdToRank(planId: string | null | undefined): number {
  if (!planId) return 0;
  return PLAN_TIER_RANK[planId.toLowerCase()] ?? 0;
}

export function requiredTierRank(tier: PlanTierId): number {
  if (tier === "all") return 0;
  return PLAN_TIER_RANK[tier] ?? 0;
}

export function formatPlanTierLabel(tier: string): string {
  return TIER_DISPLAY[tier.toLowerCase()] ?? tier;
}

/** Boolean plan feature (FM SmPlanLimits flags). */
export function resolveBooleanPlanGate(
  tenantPlanId: string | null | undefined,
  requiredTier: PlanTierId,
): CapabilityGateState {
  if (requiredTier === "all") return "included";
  return planIdToRank(tenantPlanId) >= requiredTierRank(requiredTier) ? "included" : "locked_feature";
}

/** Usage gauge → gate state from percent + counts. */
export function resolveUsageGateState(
  current: number,
  effectiveLimit: number | null,
  percent: number | null,
): CapabilityGateState {
  if (effectiveLimit == null || effectiveLimit <= 0) return "included";
  if (current >= effectiveLimit) return "limit_reached";
  const pct = percent ?? (current / effectiveLimit) * 100;
  if (pct >= 95) return "limit_reached";
  if (pct >= 82) return "near_limit";
  if (pct >= 70) return "upgrade_available";
  return "included";
}

export interface CapabilityUpsellCopy {
  headline: string;
  benefits: string[];
  ctaLabel?: string;
}
